import { payments } from '@lawbee/domain';
import { Badge, Button, Card, EmptyState, ErrorState, LoadingState, Status, StatusMarker, cn, formatDate, formatMoney, useToast, type StatusTone } from '@lawbee/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, CalendarDays, ChevronRight, Download, FileText, FolderOpen, Info, LockKeyhole, MessageSquareText, Receipt, Upload } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { api, apiUrl, ApiError, errorMessage } from '../../lib/api';
import { useParam } from '../../lib/hooks';
import { usePortalMe } from '../../shell/PortalLayout';

type PortalMatterSummary = { id: string; number: string; title: string; stageLabel: string | null; status: string };

type PortalMatterDetail = {
  matter: { id: string; number: string; title: string; status: string; stageKey: string | null; stageLabel?: string | null; stages: { key: string; label: string }[]; lawyerName: string | null };
  updates: { id: string; subject: string | null; body: string; sentAt: string | null; channel: string }[];
  deadlines: { id: string; title: string; dueOn: string }[];
  documentRequests: { id: string; title: string; description: string | null; status: string; dueOn: string | null }[];
  documents: { id: string; name: string; createdAt: string; uploadedByClient: boolean }[];
  invoices: { id: string; number: string; description: string; amountCents: number; paidCents: number; status: string; kind: string; destination?: 'trust' | 'operating' }[];
  notice?: string;
};

const MATTERS_KEY = ['portal', 'matters'] as const;
const matterKey = (id: string) => ['portal', 'matter', id] as const;

// ---------------------------------------------------------------------------
// Matter list
// ---------------------------------------------------------------------------

export function PortalMattersPage() {
  const me = usePortalMe();
  const q = useQuery({ queryKey: MATTERS_KEY, queryFn: () => api.get<{ matters: PortalMatterSummary[] }>('/portal-api/matters') });
  const firstName = me.data?.contact.firstName;

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.025em]">{firstName ? `Hello, ${firstName}` : 'Hello'}</h1>
        <p className="text-[15px] text-muted-foreground">Here is where things stand with your matters.</p>
      </header>
      {q.isLoading ? (
        <LoadingState label="Loading your matters" />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (q.data?.matters ?? []).length === 0 ? (
        <EmptyState
          icon={<FolderOpen />}
          title="No matters to show yet"
          description="When your lawyer opens a matter for you, it will appear here with updates, dates and documents."
        />
      ) : (
        <ul className="space-y-3">
          {q.data!.matters.map((m) => (
            <li key={m.id}>
              <Link
                to="/portal/app/matters/$id"
                params={{ id: m.id }}
                className="flex items-center gap-4 rounded-xl bg-card px-5 py-4 shadow-card transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/25"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[16px] font-semibold">{m.title}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                    <span className="figures">{m.number}</span>
                    {m.status === 'closed' ? (
                      <Status tone="done">Closed</Status>
                    ) : m.stageLabel ? (
                      <Status tone="progress">{m.stageLabel}</Status>
                    ) : (
                      <Status tone="idle">Open</Status>
                    )}
                  </p>
                </div>
                <ChevronRight className="size-5 shrink-0 text-muted-foreground" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Matter detail
// ---------------------------------------------------------------------------

function Section({ icon, title, children, aside }: { icon: ReactNode; title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-[16px] font-semibold [&_svg]:size-4 [&_svg]:text-muted-foreground">
          {icon}
          {title}
        </h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Quiet({ children }: { children: ReactNode }) {
  return <p className="rounded-xl bg-muted/50 px-4 py-3.5 text-sm text-muted-foreground">{children}</p>;
}

function StageProgress({ stages, stageKey, closed }: { stages: { key: string; label: string }[]; stageKey: string | null; closed: boolean }) {
  if (stages.length === 0) return null;
  const current = closed ? stages.length : stages.findIndex((s) => s.key === stageKey);
  return (
    <ol className="flex flex-col gap-0 sm:flex-row sm:gap-0" aria-label="Matter progress">
      {stages.map((s, i) => {
        const done = i < current;
        const active = i === current;
        const tone: StatusTone = done ? 'done' : active ? 'progress' : 'idle';
        const last = i === stages.length - 1;
        return (
          <li key={s.key} className="relative flex gap-3 pb-4 last:pb-0 sm:flex-1 sm:flex-col sm:items-center sm:gap-2 sm:pb-0 sm:text-center" aria-current={active ? 'step' : undefined}>
            {/* connector: vertical on mobile, horizontal from sm up */}
            {!last ? (
              <span
                aria-hidden
                className={cn(
                  'absolute left-[8px] top-5 h-[calc(100%-12px)] w-px sm:left-[calc(50%+14px)] sm:top-[9px] sm:h-px sm:w-[calc(100%-28px)]',
                  done ? 'bg-success' : 'bg-border',
                )}
              />
            ) : null}
            <span className="relative z-10 flex size-[18px] shrink-0 items-center justify-center bg-card">
              <StatusMarker tone={tone} className="size-[18px]" />
            </span>
            <span className={cn('text-sm leading-snug sm:px-1 sm:text-[13px]', active ? 'font-semibold text-foreground' : done ? 'text-foreground/80' : 'text-muted-foreground')}>
              {s.label}
              {active ? <span className="block text-xs font-normal text-muted-foreground sm:hidden">Current stage</span> : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function requestStatus(status: string): { tone: StatusTone; label: string } {
  switch (status) {
    case 'received':
      return { tone: 'done', label: 'Received' };
    case 'accepted':
    case 'complete':
    case 'completed':
      return { tone: 'done', label: 'Complete' };
    case 'open':
      return { tone: 'risk', label: 'Needed from you' };
    default:
      return { tone: 'idle', label: status.replace(/_/g, ' ') };
  }
}

function UploadButton({ requestId, matterId, label }: { requestId: string; matterId: string; label: string }) {
  const input = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const upload = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.set('file', file, file.name);
      return api.upload<{ ok: boolean }>(`/portal-api/document-requests/${encodeURIComponent(requestId)}/upload`, fd);
    },
    onSuccess: async () => {
      toast.success('Document sent', 'Your lawyer has been notified.');
      await qc.invalidateQueries({ queryKey: matterKey(matterId) });
    },
    onError: (err) => toast.error('Upload failed', errorMessage(err)),
    onSettled: () => {
      if (input.current) input.current.value = '';
    },
  });
  return (
    <>
      <input
        ref={input}
        type="file"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
        }}
      />
      <Button variant="outline" size="sm" loading={upload.isPending} onClick={() => input.current?.click()} className="w-full sm:w-auto">
        <Upload /> {label}
      </Button>
    </>
  );
}

function PayInvoiceButton({ invoiceId, dueCents }: { invoiceId: string; dueCents: number }) {
  const toast = useToast();
  const [redirecting, setRedirecting] = useState(false);
  const checkout = useMutation({
    mutationFn: () => api.post<{ url: string }>(`/portal-api/invoices/${encodeURIComponent(invoiceId)}/checkout`),
    onSuccess: (res) => {
      setRedirecting(true);
      window.location.assign(res.url);
    },
    onError: (err) => toast.error('Could not start payment', errorMessage(err)),
  });
  return (
    <Button size="sm" loading={checkout.isPending || redirecting} onClick={() => checkout.mutate()} className="w-full sm:w-auto">
      <LockKeyhole /> Pay {formatMoney(dueCents)}
    </Button>
  );
}

export function PortalMatterPage() {
  const id = useParam('id');
  const q = useQuery({
    queryKey: matterKey(id),
    queryFn: () => api.get<PortalMatterDetail>(`/portal-api/matters/${encodeURIComponent(id)}`),
    retry: (count, err) => !(err instanceof ApiError && (err.status === 404 || err.status === 403)) && count < 2,
  });

  const back = (
    <Link to="/portal/app" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
      <ArrowLeft className="size-4" aria-hidden /> All matters
    </Link>
  );

  if (q.isLoading) return <LoadingState label="Loading your matter" />;
  if (q.error) {
    const notFound = q.error instanceof ApiError && (q.error.status === 404 || q.error.status === 403);
    return (
      <div className="space-y-6">
        {back}
        {notFound ? (
          <EmptyState icon={<FolderOpen />} title="Matter not found" description="This matter is not available in your portal. It may have been closed or shared with someone else." />
        ) : (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        )}
      </div>
    );
  }
  const { matter, updates, deadlines, documentRequests, documents, invoices } = q.data!;
  const closed = matter.status === 'closed';
  const sortedUpdates = [...updates].sort((a, b) => (b.sentAt ?? '').localeCompare(a.sentAt ?? ''));
  const openRequests = documentRequests.filter((r) => r.status === 'open').length;
  const visibleInvoices = invoices.filter((v) => v.status !== 'void' && v.status !== 'cancelled');

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        {back}
        <header className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="figures text-sm text-muted-foreground">{matter.number}</span>
            {closed ? <Badge>Closed</Badge> : null}
          </div>
          <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.025em] sm:text-[30px]">{matter.title}</h1>
          {matter.lawyerName ? (
            <p className="text-[15px] text-muted-foreground">
              Your lawyer: <span className="font-medium text-foreground">{matter.lawyerName}</span>
            </p>
          ) : null}
        </header>
      </div>

      {matter.stages.length > 0 ? (
        <Card className="p-5 sm:p-6">
          <p className="mb-4 text-sm font-medium text-muted-foreground">{closed ? 'This matter is closed' : 'Where things stand'}</p>
          <StageProgress stages={matter.stages} stageKey={matter.stageKey} closed={closed} />
        </Card>
      ) : null}

      {openRequests > 0 ? (
        <div className="flex items-start gap-3 rounded-xl bg-accent px-4 py-3.5 text-sm">
          <Info className="mt-0.5 size-4 shrink-0 text-accent-foreground" aria-hidden />
          <p>
            Your lawyer is waiting for <span className="figures font-semibold">{openRequests}</span> {openRequests === 1 ? 'document' : 'documents'} from you. See
            Documents requested below.
          </p>
        </div>
      ) : null}

      <div className="grid gap-8 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-8">
          <Section icon={<MessageSquareText />} title="Updates">
            <p className="text-xs text-muted-foreground">Updates are for your information and are not legal advice. Contact your lawyer with any questions.</p>
            {sortedUpdates.length === 0 ? (
              <Quiet>No updates yet. Your lawyer will post updates here as your matter moves forward.</Quiet>
            ) : (
              <ol className="space-y-3">
                {sortedUpdates.map((u) => (
                  <li key={u.id}>
                    <Card className="p-5">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <p className="font-semibold">{u.subject || 'Update on your matter'}</p>
                        {u.sentAt ? <time className="figures text-xs text-muted-foreground" dateTime={u.sentAt}>{formatDate(u.sentAt)}</time> : null}
                      </div>
                      <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-foreground/90">{u.body}</p>
                    </Card>
                  </li>
                ))}
              </ol>
            )}
          </Section>

          <Section icon={<FileText />} title="Documents requested">
            {documentRequests.length === 0 ? (
              <Quiet>Your lawyer has not asked for any documents.</Quiet>
            ) : (
              <ul className="space-y-3">
                {documentRequests.map((r) => {
                  const st = requestStatus(r.status);
                  return (
                    <li key={r.id}>
                      <Card className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 space-y-1">
                          <p className="font-semibold">{r.title}</p>
                          {r.description ? <p className="whitespace-pre-line text-sm text-muted-foreground">{r.description}</p> : null}
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-[13px]">
                            <Status tone={st.tone}>{st.label}</Status>
                            {r.dueOn ? (
                              <span className="text-muted-foreground">
                                Please send by <span className="figures">{formatDate(r.dueOn)}</span>
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div className="shrink-0">
                          <UploadButton requestId={r.id} matterId={matter.id} label={r.status === 'open' ? 'Upload' : 'Upload another'} />
                        </div>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>
        </div>

        <div className="space-y-8">
          <Section icon={<CalendarDays />} title="Upcoming dates">
            {deadlines.length === 0 ? (
              <Quiet>No upcoming dates.</Quiet>
            ) : (
              <ul className="divide-y rounded-xl bg-card shadow-card">
                {deadlines.map((d) => (
                  <li key={d.id} className="flex items-start justify-between gap-3 px-4 py-3">
                    <span className="text-sm">{d.title}</span>
                    <span className="figures shrink-0 text-sm font-medium">{formatDate(d.dueOn)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section icon={<Receipt />} title="Invoices">
            {visibleInvoices.length === 0 ? (
              <Quiet>Nothing to pay.</Quiet>
            ) : (
              <ul className="space-y-3">
                {visibleInvoices.map((v) => {
                  const due = Math.max(0, v.amountCents - v.paidCents);
                  const payable = v.status === 'open' && due > 0;
                  return (
                    <li key={v.id}>
                      <Card className="space-y-3 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{v.description}</p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              <span className="figures">{v.number}</span>
                              <span aria-hidden> · </span>
                              {(payments.PAYMENT_KIND_LABELS as Record<string, string>)[v.kind] ?? v.kind}
                              {v.destination === 'trust' ? ' · held in trust' : ''}
                            </p>
                          </div>
                          {payable ? null : <Status tone="done">Paid</Status>}
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-sm">
                            {payable ? 'Amount due ' : 'Amount '}
                            <span className="figures font-semibold">{formatMoney(payable ? due : v.amountCents)}</span>
                          </p>
                          {payable ? <PayInvoiceButton invoiceId={v.id} dueCents={due} /> : null}
                        </div>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>

          <Section icon={<FolderOpen />} title="Your documents">
            {documents.length === 0 ? (
              <Quiet>No documents shared yet.</Quiet>
            ) : (
              <ul className="divide-y rounded-xl bg-card shadow-card">
                {documents.map((d) => (
                  <li key={d.id}>
                    <a
                      href={apiUrl(`/portal-api/documents/${encodeURIComponent(d.id)}`)}
                      className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/60"
                      download
                    >
                      <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{d.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {d.uploadedByClient ? 'Sent by you' : 'From your lawyer'} · <span className="figures">{formatDate(d.createdAt)}</span>
                        </span>
                      </span>
                      <Download className="size-4 shrink-0 text-muted-foreground" aria-label="Download" />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}
